<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { toast } from 'dashboard/composables/useToast';
import {
  PERMISSION_GROUPS,
  CONVERSATION_MANAGE,
  CONVERSATION_UNASSIGNED_MANAGE,
  CONVERSATION_PARTICIPATING_MANAGE,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);
const expandedGroups = ref({});

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

// Initialize all groups as expanded
onMounted(() => {
  Object.keys(PERMISSION_GROUPS).forEach(key => {
    expandedGroups.value[key] = true;
  });
});

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
};

const toggleGroup = groupKey => {
  expandedGroups.value[groupKey] = !expandedGroups.value[groupKey];
};

const isGroupExpanded = groupKey => {
  return expandedGroups.value[groupKey] !== false;
};

const getGroupSelectedCount = group => {
  return group.permissions.filter(p => selectedPermissions.value.includes(p))
    .length;
};

const isGroupAllSelected = group => {
  return group.permissions.every(p => selectedPermissions.value.includes(p));
};

const isGroupPartiallySelected = group => {
  const count = getGroupSelectedCount(group);
  return count > 0 && count < group.permissions.length;
};

const toggleGroupAll = group => {
  if (isGroupAllSelected(group)) {
    // Deselect all in group
    selectedPermissions.value = selectedPermissions.value.filter(
      p => !group.permissions.includes(p)
    );
  } else {
    // Select all in group
    const newPerms = new Set([
      ...selectedPermissions.value,
      ...group.permissions,
    ]);
    selectedPermissions.value = [...newPerms];
  }
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    const hasAddedManageAllConversation =
      newValue.includes(CONVERSATION_MANAGE) &&
      !oldValue.includes(CONVERSATION_MANAGE);

    if (hasAddedManageAllConversation) {
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_MANAGE,
          CONVERSATION_PARTICIPATING_MANAGE,
        ]),
      ];
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      toast.success(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      toast.success(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    toast.error(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto max-h-[80vh]">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="flex flex-col gap-1 mb-4 mt-2">
          <div
            v-for="(group, groupKey) in PERMISSION_GROUPS"
            :key="groupKey"
            class="border border-n-weak rounded-lg overflow-hidden"
          >
            <!-- Group Header -->
            <div
              class="flex items-center justify-between px-3 py-2 bg-n-alpha-1 cursor-pointer select-none"
              @click="toggleGroup(groupKey)"
            >
              <div class="flex items-center gap-2">
                <input
                  type="checkbox"
                  :checked="isGroupAllSelected(group)"
                  :indeterminate="isGroupPartiallySelected(group)"
                  class="cursor-pointer"
                  @click.stop="toggleGroupAll(group)"
                />
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ group.label }}
                </span>
                <span class="text-xs text-n-slate-9">
                  ({{ getGroupSelectedCount(group) }}/{{
                    group.permissions.length
                  }})
                </span>
              </div>
              <span
                class="text-n-slate-9 transition-transform"
                :class="{ 'rotate-180': isGroupExpanded(groupKey) }"
              >
                <i class="i-lucide-chevron-down w-4 h-4" />
              </span>
            </div>

            <!-- Group Permissions -->
            <div
              v-if="isGroupExpanded(groupKey)"
              class="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2"
            >
              <div
                v-for="permission in group.permissions"
                :key="permission"
                class="flex items-center"
              >
                <input
                  :id="permission"
                  v-model="selectedPermissions"
                  type="checkbox"
                  :value="permission"
                  name="permissions"
                  class="ltr:mr-2 rtl:ml-2"
                />
                <label
                  :for="permission"
                  class="text-xs font-normal text-n-slate-11 cursor-pointer"
                >
                  {{
                    $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
                  }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
